import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';

export default class CurrentlyPlayingService extends Service {
  @service stereo;
  @service session;
  @service platform;

  @tracked bounce;
  @tracked loadedSounds = {};
  @tracked playQueue = A([]);
  @tracked playlist;

  get currentSound() {
    return this.stereo.currentSound;
  }

  addEventHandlers() {
    //console.log('currentlyPlaying.addEventHandlers')
    this.stereo.on('audio-ended', this.handleAudioEnded.bind(this));
    //this.stereo.on('pre-load', this.debugPreLoad.bind(this));
    //this.stereo.on('new-load-request', this.debugNewLoadRequest.bind(this));
  }

  handleAudioEnded(sound) {
    //console.log('we just got audio-ended on sound ', sound);
    if (this.playlist) {
      // Remove the thing that just finised playing
      this.playlist.playlistEntries.shiftObject();
    }
    this.playNext();
  }

  debugPreLoad(urlsToTry) {
    console.log('debugPreLoad urlsToTry = ', urlsToTry);
  }

  debugNewLoadRequest({ loadPromise, urlsOrPromise, options }) {
    console.log('debugNewLoadRequest loadPromise = ', loadPromise);
    console.log('debugNewLoadRequest urlsOrPromise = ', urlsOrPromise);
    console.log('debugNewLoadRequest options = ', options);
  }

  async queueBounce(bounce) {
    //console.log('starting queueBounce for bounce', bounce.id);
    this.playQueue.pushObject(bounce);
  }

  async queuePlaylist(playlist) {
    //console.log('starting queuePlaylist for playlist', playlist.id);
    let queueData = {
      playlist: playlist,
      playlistEntries: A([]),
    };
    for (let i = 0, len = playlist.playlistEntries.length; i < len; i++) {
      let playlistEntry = playlist.playlistEntries[i];
      let project = playlistEntry.project;
      let bounce = playlistEntry.project.currentBounce;
      queueData.playlistEntries.pushObject({
        playlistEntry: playlistEntry,
        project: project,
        bounce: bounce,
      });
    }
    this.playQueue.pushObject(queueData);
    if (!this.stereo.isPlaying) {
      this.playNext();
    }
  }

  removePlaylist(playlist) {
    if (this.playlist === playlist) {
      this.stereo.pause();
      this.bounce.percentPlayed = 0;
      this.bounce = null;
      this.playlist = null;
      if (this.currentSound) {
        console.log(
          'we have a currentSound',
          this.currentSound,
          this.currentSound.currentTime,
        );
        this.currentSound.position = 0;
      }
      this.playNext();
    } else {
      this.playQueue.removeObject(playlist);
    }
  }

  removeBounce(bounce) {
    if (this.bounce === bounce) {
      this.stereo.pause();
      this.bounce.percentPlayed = 0;
      this.bounce = null;
      if (this.currentSound) {
        console.log(
          'we have a currentSound',
          this.currentSound,
          this.currentSound.currentTime,
        );
        this.currentSound.position = 0;
      }
      this.playNext();
    } else {
      this.playQueue.removeObject(bounce);
    }
  }

  async skipToNextTrack() {
    this.stereo.pause();
    if (this.currentSound) {
      console.log(
        'we have a currentSound',
        this.currentSound,
        this.currentSound.currentTime,
      );
      this.currentSound.position = 0;
    }
    this.bounce.percentPlayed = 0;
    this.bounce = null;
    if (this.playlist) {
      this.playlist.playlistEntries.shiftObject();
    } else {
      // Do nothing?
    }
    this.playNext();
  }

  async playNext() {
    //console.log('starting playNext and this.playlist = ', this.playlist)
    if (
      this.playlist &&
      this.playlist.playlist &&
      this.playlist.playlistEntries.length
    ) {
      let nextUp = this.playlist.playlistEntries.objectAt(0);
      //console.log('pulling from the current playlist for nextUp ', nextUp);
      if (!nextUp) {
        //console.log('nothing is up next')
        this.bounce = null;
        this.playlist = null;
        return;
      }
      let bounce = nextUp.bounce;
      await this.playBounce(bounce);

      let nextBounce = this.playlist.playlistEntries[1];
      // We check platform here because mobile Safari will stop the currently playing sound as soon
      // as we try to load a different sound. It seems to be stuck on using a single audio element
      // for all sounds.
      if (nextBounce && this.platform.isNative) {
        this.loadBounce(nextBounce.bounce);
      }
    } else {
      let nextUp = this.playQueue.shiftObject();
      //console.log('pulling from the play queue for nextUp ', nextUp);
      if (nextUp && nextUp.playlist && nextUp.playlistEntries.length) {
        this.playlist = nextUp;
        this.playNext();
      } else if (nextUp) {
        //console.log('next up in the queue was NOT a playlist', nextUp);
        this.playBounce(nextUp);
      } else {
        //console.log('nothing is up next')
        this.bounce = null;
        this.playlist = null;
        return;
      }
    }
  }

  async loadBounce(bounce) {
    //console.log('we are going to load the sound for bounce', bounce)

    let identifier = this.identifierForBounce(bounce);
    let stereoSound = await this.stereo.load(identifier, {
      metadata: {
        title: bounce.fileName,
        artist: 'via Seshy',
        artwork: [{ src: '/images/seshy_robo.png' }],
      },
    });
    let sound = stereoSound.sound;
    this.addSound(sound, identifier, bounce.soundId);
    bounce.set('loadedSound', sound);
    return sound;
  }

  async playBounce(bounce) {
    let sound = bounce.get('loadedSound');
    if (!sound) {
      sound = await this.loadBounce(bounce);
    }
    sound.play();
    this.set('bounce', bounce);
  }

  async togglePlayBounce(bounce) {

    let sound = bounce.get('loadedSound');
    if (!sound) {
      sound = await this.loadBounce(bounce);
    }
    sound.togglePause();

    // If we're toggling the currently-playing bounce on or off we bail out.
    if(this.bounce == bounce){
      return;
    }

    // If the bounce is currently in the play queue just move
    // it to the top instead of duplicating it.
    // TODO: What sould we do if the bounce is part of a playlist
    // that's in the queue...
    this.playQueue.removeObject(bounce);

    if (this.playlist) {
      this.playQueue.unshiftObject(this.playlist);
      this.playlist = null;
    } else if (this.bounce) {
      this.playQueue.unshiftObject(this.bounce);
    }

    this.set('bounce', bounce);
  }

  async play() {
    this.playNext();
    return;

    /*
    let nextUp = this.playQueue.shiftObject();
    //console.log('playing for nextUp ', nextUp);
    //let identifier = this.identifierForBounce(bounce);
    //console.log('identifier = ', identifier);
    //this.stereo.play(identifier);
    let sound = bounce.get('loadedSound');
    if(!sound){
      //console.log('we have to loooooook for the sound')

      let identifier = this.identifierForBounce(bounce);
      let stereoSound = await this.stereo.load( identifier, {
        metadata: {
          title: bounce.fileName,
          artist: 'via Seshy',
          artwork: [{ src: '/images/seshy_robo.png' }],
        }
      });
      sound = stereoSound.sound;
      this.addSound(sound, identifier, bounce.soundId)
    }else{
      //console.log('we had a pre-loaded sound', sound.url);
    }
    //console.log('the sound = ', sound);
    //this.stereo.play(sound);
    sound.play();
    this.set('bounce', bounce);
    */

    /*
    // We check platform here because mobile Safari will stop the currently playing sound as soon
    // as we try to load a different sound. It seems to be stuck on using a single audio element
    // for all sounds.
    if(this.platform.isNative){
      let nextBounce = this.playQueue.objectAt(0);
      if(nextBounce){
        //console.log('we are going to load the next sound')

        let identifier = this.identifierForBounce(nextBounce);
        let stereoSound = await this.stereo.load( identifier, {
          metadata: {
            title: nextBounce.fileName,
            artist: 'via Seshy',
            artwork: [{ src: '/images/seshy_robo.png' }],
          }
        });
        sound = stereoSound.sound;
        this.addSound(sound, identifier, nextBounce.soundId)
        nextBounce.set('loadedSound', sound);
      }
    }
    */
  }

  identifierForBounce(bounce) {
    return `${bounce.downloadUrl}?Authorization=Bearer ${this.session.data.authenticated.access_token}`;
  }

  addSound(sound, identifier, sound_id) {
    this.loadedSounds[sound_id] = { sound, identifier };
    this.loadedSounds = this.loadedSounds;
  }

  getSound(sound_id) {
    //console.log('in currentlyPlaying looking for sound: ', sound_id);
    let data = this.loadedSounds[sound_id];
    //console.log('found sound', data)
    if (data) {
      return data.sound;
    }
    return null;
  }

  getIdentifier(sound_id) {
    //console.log('in currentlyPlaying looking for identifier: ', sound_id);
    let data = this.loadedSounds[sound_id];
    //console.log('found sound', data)
    if (data) {
      return data.identifier;
    }
    return null;
  }

  unloadSound(sound_id) {
    delete this.loadedSounds[sound_id];
    this.loadedSounds = this.loadedSounds;
  }
}
